import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { H3, H2, Caption, LabelRegular, Box } from '@comeon/ui';
import { authenticationSelector } from '~/components/authentication/ducks/selectors.js';
import WeSpinGameRoomNextSessionList from './we-spin-game-room-next-session-list.js';
import WeSpinFlag from '../we-spin-flag.js';
import generateSrcSet from '../../../../utils/generateSrcSet.js';
import WeSpinGameRoomFollowStreamerSwitch from './we-spin-game-room-follow-streamer-switch.js';
import useTranslations from '../../../translation/use-translations';
import { WE_SPIN_FOLLOW_STREAMER_TEXT_KEYS } from '../constants/we-spin-text-keys.js';
import {
	followStreamer,
	selectIsCurrentStreamerFollowed,
	unfollowStreamer,
	selectCurrentStreamerProfile
} from '../ducks/we-spin-streamer-duck.js';
import { selectConfiguration } from '../ducks/we-spin-selectors.js';

const SIZES = '180px';

const WeSpinGameRoomStreamer = ({ streamView, resources }) => {
	const { streamerImgUrl, streamerName, streamerDescription, language, streamerRef } = streamView;

	const { hasWeSpinStreamerFollowers } = useSelector(selectConfiguration);
	const isAuthenticated = useSelector(authenticationSelector);
	const dispatch = useDispatch();

	const isStreamerFollowed = useSelector(selectIsCurrentStreamerFollowed);

	const currentStreamerProfile = useSelector(selectCurrentStreamerProfile);

	const onChangeStreamerFollowed = useCallback(() => {
		dispatch(!isStreamerFollowed ? followStreamer(streamerRef) : unfollowStreamer(streamerRef));
	}, [dispatch, isStreamerFollowed, streamerRef]);

	const { translations, isLoadingTranslations } = useTranslations(WE_SPIN_FOLLOW_STREAMER_TEXT_KEYS);

	const srcSet = useMemo(() => streamerImgUrl && generateSrcSet(streamerImgUrl, true), [streamerImgUrl]);

	return isLoadingTranslations ? null : (
		<>
			<div className="avatar-circle u-div-c u-b-radius-round u-relative u-mb1 u-mt2">
				{hasWeSpinStreamerFollowers && !!currentStreamerProfile?.noFollowers && (
					<div className="avatar-circle__overlay">
						<div className="avatar-circle__overlay__followers">
							<div>
								<Caption>{translations.WE_SPIN_FOLLOW_STREAMER_FOLLOWERS}</Caption>
							</div>
							<div>
								<H2>{currentStreamerProfile?.noFollowers}</H2>
							</div>
						</div>
					</div>
				)}
				<div className="avatar-circle__flag u-pos-c u-b-radius">
					<WeSpinFlag language={language} />
				</div>
				<img
					alt=""
					srcSet={srcSet}
					sizes={SIZES}
					className="u-b-radius-round u-object-fit-cover"
					src={streamerImgUrl}
				/>
			</div>
			<div className="streamer-presentation u-text-c u-pl1 u-pr1">
				<H3 mt={2} mb={3}>
					{streamerName}
				</H3>
				<LabelRegular my={1}>{streamerDescription}</LabelRegular>
			</div>
			{hasWeSpinStreamerFollowers && isAuthenticated && (
				<Box px="5" py="3">
					<WeSpinGameRoomFollowStreamerSwitch
						isStreamerFollowed={isStreamerFollowed}
						onChangeStreamerFollowed={onChangeStreamerFollowed}
						streamerName={currentStreamerProfile?.screenName}
						translations={translations}
					/>
				</Box>
			)}
			<WeSpinGameRoomNextSessionList
				currentStreamersFirst
				showGameIcon
				resources={resources}
				streamerName={streamerName}
			/>
		</>
	);
};

export default WeSpinGameRoomStreamer;
